import ZTable, { IColumnType, IFilterItem, ITableRef } from '@/components/ZTable';
import { t } from 'i18next';
import { dateTimeDisplayFormat, formatApiDate, formatDate, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import MainContent from '@/layouts/MainContent';
import { Form, Button, Modal, Spin, Row, Col, Input, InputNumber, Select, Checkbox, Card, DatePicker, Space, message } from 'antd';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { FlagOutlined, PlusCircleOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import { CreateUpdateLianeAdvertisingModel, LianeAdvertisingDto, LianeAdvertisingDtoPagingResponse, LianeTicketPagingDto } from '@/__generated';
import { AdvertisingClickActionType, AdvertisingDetailType, AdvertisingScreenType, AdvertisingType, VideoPlayerType } from '@/constants';
import BooleanTag from '@/components/app/BooleanTag';
import { apiSdk } from '@/httpClient';
import FormItem from 'antd/es/form/FormItem';
import AppDatePicker from '@/components/FormItems/DatePicker';
import CountryInput from '@/components/FormItems/CountryInput';
import RegionInput from '@/components/FormItems/RegionInput';
import CityInput from '@/components/FormItems/CityInput';
import AdvertisingDetailItem from './comonents/AdvertisingDetailItem';

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'title', get label() { return t('Title'); } },
  { type: 'LIST', name: 'advertisingScreen', get label() { return t('Screen'); }, listData: AdvertisingScreenType.__getSelectOptions() },
  { type: 'LIST', name: 'advertisingType', get label() { return t('Type'); }, listData: AdvertisingType.__getSelectOptions() },
];

const PAGE_TITLE = 'Passenger App Advertising';
const columns: IColumnType<LianeAdvertisingDto>[] = [
  {
    title: 'Title',
    dataIndex: 'title',
    ellipsis: true,
    width: 300,
  },
  {
    title: 'Order',
    dataIndex: 'order',
    ellipsis: true,
    width: 100,
  },
  {
    title: 'Screen',
    dataIndex: 'advertisingScreen',
    ellipsis: true,
    width: 150,
    render: v => AdvertisingScreenType.__getValue(v),
  },
  {
    title: 'Type',
    dataIndex: 'advertisingType',
    ellipsis: true,
    width: 150,
    render: v => AdvertisingType.__getValue(v),
  },
  {
    title: 'Is Active',
    dataIndex: 'isActive',
    ellipsis: true,
    width: 150,
    render: v => <BooleanTag value={v} />,
  },
  {
    title: 'Created on',
    dataIndex: 'createdOn',
    ellipsis: true,
    width: 150,
    render: v => dateTimeDisplayFormat.dateTime(v),
  },
];
export default function AdvertisingPage() {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [advertising, setAdvertising] = React.useState<CreateUpdateLianeAdvertisingModel>();
  const [loading, setLoading] = useState(false);

  const ediAdvertising = (id: string) => {
    setFormVisible(true);
    setLoading(true);
    apiSdk.LianeAdvertisingService.getDetails({ id: id })
      .then(res => {
        setAdvertising(res);
        form.setFieldsValue({
          ...res,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const toSubmitValue = v => {
    return {
      ...v,
      advertisingDetails:
        v.advertisingDetails?.map(v => ({
          ...v,
          startDate: v.startDate ? formatApiDate(v.startDate) : undefined,
          endDate: v.endDate ? formatApiDate(v.endDate) : undefined,
        })) || [],
    };
  };
  const updateEntity = (values: any) => {
    apiSdk.LianeAdvertisingService.update({ body: toSubmitValue(values) })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          setAdvertising(undefined);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const createEntity = (values: any) => {
    apiSdk.LianeAdvertisingService.create({ body: toSubmitValue(values) })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          setAdvertising(undefined);

          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={[
        <Button
          key="create"
          type="primary"
          className="nta-create-btn"
          icon={<PlusCircleOutlined />}
          onClick={() => {
            form.setFieldsValue({
              advertisingDetails: [
                {
                  advertisingDetailType: AdvertisingDetailType.Image,
                  clickAction: AdvertisingClickActionType.ExternalUrl,
                },
              ],
            });
            setAdvertising(undefined);
            setFormVisible(true);
          }}
        >
          {t('createNew')}
        </Button>,
      ]}
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<LianeAdvertisingDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/liane-advertising/get-paging"
          bordered
          order_by="createdOn"
          order_by_asc={false}
          allowEdit={true}
          onEdit={record => {
            ediAdvertising(record.id || '');
          }}
          allowDelete={true}
          delete_url="/liane-advertising/delete"
          filters={filters}
        />
      </div>

      {formVisible ? (
        <Modal
          open={formVisible}
          maskClosable={false}
          style={{ top: 10 }}
          width={ModalWidth.extraLarge}
          className="nta-custom-form"
          title={advertising ? 'Update' : 'Create new'}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEntity(values);
                } else {
                  createEntity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Spin spinning={loading}>
            <Form form={form} size="middle" layout="vertical">
              <Form.Item name="id" className="hidden">
                <Input type="hidden" />
              </Form.Item>
              <Row gutter={ROW_GUTTER}>
                <Col md={6}>
                  <Row gutter={ROW_GUTTER}>
                    <Col md={24}>
                      <Form.Item label={t('Title')} name="title" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col md={12}>
                      <Form.Item label={t('Order')} name="order" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <InputNumber className="w-100" />
                      </Form.Item>
                    </Col>
                    <Col md={12}>
                      <Form.Item label="   " name="isActive" valuePropName="checked">
                        <Checkbox>{t('Is Active')}</Checkbox>
                      </Form.Item>
                    </Col>
                    <Col md={24}>
                      <Form.Item label={t('Screen')} name="advertisingScreen" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <Select options={AdvertisingScreenType.__getSelectOptions()} />
                      </Form.Item>
                    </Col>
                    <Col md={24}>
                      <Form.Item label={t('Type')} name="advertisingType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <Select options={AdvertisingType.__getSelectOptions()} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col md={18}>
                  <Form.List name="advertisingDetails">
                    {(fields, { add, remove }) => (
                      <div>
                        <div style={{ marginBottom: 10, textAlign: 'right' }}>
                          <Button
                            onClick={() =>
                              add({
                                advertisingDetailType: AdvertisingDetailType.Image,
                                clickAction: AdvertisingClickActionType.ExternalUrl,
                              })
                            }
                            icon={<PlusCircleOutlined />}
                            type="primary"
                            size="small"
                          >
                            Add
                          </Button>
                        </div>
                        {fields.map(field => {
                          return <AdvertisingDetailItem canDelete={fields.length > 1} form={form} key={field.key} field={field} remove={remove} />;
                        })}
                      </div>
                    )}
                  </Form.List>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Modal>
      ) : null}
    </MainContent>
  );
}
